import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { useSnackbar } from 'notistack';
import { Box, Button, Card, Container, Grid, MenuItem, Tab, Tabs, TextField } from '@mui/material';
import { a11yProps, TabPanels } from 'components/mui/Tabs';
import useAuth from 'hooks/useAuth';
import useSwiperTabs from 'hooks/useSwiperTabs';
import auth from 'recoil/atoms/auth';


const Account = () => {
    const {t} = useTranslation();
    const {index, setIndex, setSwiper} = useSwiperTabs(0);
    const id = 'settings';

    return (
        <Container maxWidth="md" sx={{my: 4}}>
            <Card>
                <Tabs
                    variant="scrollable"
                    value={index}
                    onChange={(e, v) => setIndex(v)}
                    aria-label="Settings tabs"
                    sx={{borderRight: 1, borderColor: 'divider'}}
                >
                    <Tab label={t('account')} {...a11yProps(id, 0)} />
                    <Tab label={t('password')} {...a11yProps(id, 1)} />
                    <Tab label={t('language')} {...a11yProps(id, 1)} />
                </Tabs>

                <TabPanels
                    active={index}
                    id={id}
                    setIndex={setIndex}
                    setSwiper={setSwiper}
                    keepMounted // because slide bug if text field in tab
                    tabs={[
                        <Name/>,
                        <Password/>,
                        <Locale/>,
                        <Password/>,
                    ]}
                />
            </Card>
        </Container>
    );
};

const Name = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const {rename} = useAuth();
    const {enqueueSnackbar} = useSnackbar();

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);

        rename(
            data.get('lastName') as string,
            data.get('firstName') as string,
            data.get('phone') as string,
        )
            .then(() => enqueueSnackbar(t('success'), {
                autoHideDuration: 2000,
                variant: 'success',
            }))
    };

    return (
        <Box component="form" onSubmit={submit}>
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        id="email"
                        label={t('email')}
                        disabled
                        defaultValue={my.email}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        autoComplete="phone"
                        name="phone"
                        fullWidth
                        id="phone"
                        label={t('phone')}
                        defaultValue={my.phone}
                        type="tel"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        autoComplete="given-name"
                        name="firstName"
                        required
                        fullWidth
                        id="firstName"
                        label={t('first_name')}
                        autoFocus
                        defaultValue={my.firstName}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        fullWidth
                        id="lastName"
                        label={t('last_name')}
                        name="lastName"
                        autoComplete="family-name"
                        defaultValue={my.lastName}
                    />
                </Grid>
            </Grid>
            <Box textAlign="right">
                <Button
                    type="submit"
                    variant="contained"
                >
                    {t('save')}
                </Button>
            </Box>
        </Box>
    )
};

const Password = () => {
    const {t} = useTranslation();
    const {updatePassword} = useAuth();
    const {enqueueSnackbar} = useSnackbar();

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);

        updatePassword(
            data.get('password_old') as string,
            data.get('password') as string,
            data.get('password_confirmation') as string,
        )
            .then(() => enqueueSnackbar(t('success'), {
                autoHideDuration: 2000,
                variant: 'success',
            }))
    };

    return (
        <Box component="form" onSubmit={submit}>
            <Box mb={2}>
                <TextField
                    margin="normal"
                    required
                    type="password"
                    fullWidth
                    id="password_old"
                    label={t('current_password')}
                    name="password_old"
                    autoComplete="current-password"
                    autoFocus
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label={t('password')}
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    inputProps={{minLength: 8}}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password_confirmation"
                    label={t('password_confirmation')}
                    type="password"
                    id="password_confirmation"
                    autoComplete="new-password"
                    inputProps={{minLength: 8}}
                />
            </Box>

            <Box textAlign="right">
                <Button
                    type="submit"
                    variant="contained"
                >
                    {t('update_password')}
                </Button>
            </Box>
        </Box>
    )
};

const Locale = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const {setLocale} = useAuth();

    return (
        <TextField
            select
            id="locale"
            label={t('language')}
            fullWidth
            margin="normal"
            variant="outlined"
            value={my.settings.locale}
            onChange={e => setLocale(e.target.value)}
        >
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="ru">Русский</MenuItem>
            <MenuItem value="ja">日本語</MenuItem>
        </TextField>
    )
};

export default Account;
